import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { GuestToken } from "@/types/database";

/**
 * Fetch a guest token by its token string.
 * PUBLIC query — no authentication required.
 * The 64-char hex token string is the security credential (capability-based).
 */
export function useGuestTokenByToken(token: string | null) {
  return useQuery({
    queryKey: ["guest_tokens", token],
    enabled: !!token,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("guest_tokens")
        .select(`
          *,
          booking:bookings(
            id, guest_name, guest_email, guest_phone, guest_count,
            check_in, check_out, nights, status, fee,
            unit:units(id, unit_number, organization_id),
            host:hosts(
              id,
              profile:profiles(id, full_name, email, phone)
            )
          )
        `)
        .eq("token", token!)
        .single();
      if (error) throw error;
      return data as GuestToken;
    },
  });
}

/**
 * Update guest token check-in status (id_uploaded, rules_acknowledged).
 *
 * SECURITY: Uses the update_guest_token_checkin() SECURITY DEFINER RPC function
 * instead of a direct table UPDATE. This prevents any authenticated user from
 * modifying arbitrary guest tokens — only the token string (credential) grants
 * access to update, and only the two safe fields are exposed.
 *
 * No auth required — the token string is the credential.
 *
 * @param token - The raw 64-char hex token string (from URL param / loaded token)
 * @param idUploaded - Set id_uploaded flag (leave undefined to leave unchanged)
 * @param rulesAcknowledged - Set rules_acknowledged flag (leave undefined to leave unchanged)
 */
export function useUpdateGuestToken() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      idUploaded,
      rulesAcknowledged,
    }: {
      token: string;
      idUploaded?: boolean;
      rulesAcknowledged?: boolean;
    }) => {
      const { data, error } = await supabase.rpc("update_guest_token_checkin", {
        p_token: token,
        p_id_uploaded: idUploaded ?? null,
        p_rules_acknowledged: rulesAcknowledged ?? null,
      });
      if (error) throw error;
      return data as GuestToken;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["guest_tokens"] });
    },
  });
}

/**
 * Generate a guest token for an approved booking (host or admin action).
 * Returns the shareable guest portal URL.
 *
 * SECURITY: Uses the generate_guest_link() SECURITY DEFINER RPC function
 * instead of a direct table INSERT. The function validates that the caller
 * is authorised for the booking's org before creating the token.
 *
 * Requires authentication.
 */
export function useGenerateGuestLink() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string }) => {
      const { data, error } = await supabase.rpc("generate_guest_link", {
        p_booking_id: bookingId,
      });
      if (error) throw error;

      const guestToken = data as GuestToken;

      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["guest_tokens"] });

      return {
        token: guestToken,
        url: `${window.location.origin}/guest/${guestToken.token}`,
      };
    },
  });
}
